
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { adminError } from '@/lib/api/paper-stats';

export const signIn = async (email: string, password: string) => {
  try {
    if (!email || !password) {
      throw new Error('Email and password are required');
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Please enter a valid email address');
    }
    
    // Set flag to indicate this is a new sign-in
    sessionStorage.setItem('is_new_signin', 'true');
    
    const { error, data } = await supabase.auth.signInWithPassword({ 
      email: email.trim(), 
      password 
    });
    
    if (error) {
      // Clear the flag if sign-in fails
      sessionStorage.removeItem('is_new_signin');
      throw error;
    }
    
    if (!data?.user) {
      // Clear the flag if sign-in fails
      sessionStorage.removeItem('is_new_signin');
      throw new Error('Authentication failed');
    }
  } catch (error: any) {
    adminError('Sign in error:', error);
    toast.error(error.message || 'Error signing in');
    throw error;
  }
};

export const signInWithGoogle = async () => {
  try {
    // Set flag to indicate this is a new sign-in (will persist through redirect)
    sessionStorage.setItem('is_new_signin', 'true');

    // Honor a stored post-auth destination (Writer guest flow, invitations,
    // pricing). Only same-origin paths are allowed — never a full URL, so an
    // attacker-controlled value cannot bounce the user off-site.
    const stored = sessionStorage.getItem('redirectAfterAuth');
    const safePath =
      stored && stored.startsWith('/') && !stored.startsWith('//') ? stored : '/';

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}${safePath}`
      }
    });
    
    if (error) {
      // Clear the flag if sign-in fails
      sessionStorage.removeItem('is_new_signin');
      throw error;
    }
  } catch (error: any) {
    adminError('Google sign in error:', error);
    toast.error(error.message || 'Error signing in with Google');
    throw error;
  }
};

export const signUp = async (email: string, password: string, name?: string) => {
  try {
    const trimmedEmail = email.trim();

    // Pre-flight server-side email validation: disposable-domain blocklist,
    // MX-record check, IP rate limit. Surfaces a clean error before we hit
    // supabase.auth.signUp (whose own trigger would return a generic error).
    try {
      const { data: check, error: checkError } = await supabase.functions.invoke(
        'validate-signup-email',
        { body: { email: trimmedEmail } }
      );
      if (checkError) {
        // Don't block signup on validator outage — the auth.users trigger
        // is the safety net for disposable domains.
        adminError('validate-signup-email error (allowing):', checkError);
      } else if (check && check.ok === false) {
        const msg = check.message || 'This email address is not accepted.';
        toast.error(msg);
        return {
          data: null,
          error: { message: msg, code: check.code } as any,
          needsConfirmation: false,
        };
      }
    } catch (validationError: any) {
      adminError('validate-signup-email threw (allowing):', validationError);
    }

    const redirectUrl = `${window.location.origin}/`;

    const { data, error } = await supabase.auth.signUp({
      email: trimmedEmail,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          name: name || trimmedEmail.split('@')[0],
        }
      }
    });

    if (error) {
      // Surface the database trigger's rejection cleanly.
      const friendly = /disposable_email_rejected/i.test(error.message)
        ? 'Disposable email addresses are not accepted. Please use a permanent email address.'
        : error.message;
      toast.error(friendly);
      return { data: null, error, needsConfirmation: false };
    }

    // Check if user needs email confirmation
    const needsConfirmation = data.user && !data.user.email_confirmed_at;
    
    // If user is immediately signed in (no email confirmation required)
    if (data.user && data.user.email_confirmed_at) {
      sessionStorage.setItem('is_new_signin', 'true');
    }

    return { data, error: null, needsConfirmation };
  } catch (error: any) {
    console.error('Sign up error:', error);
    toast.error(error.message || 'Failed to create account');
    return { data: null, error, needsConfirmation: false };
  }
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
  } catch (error: any) {
    adminError('Sign out error:', error);
    toast.error(error.message || 'Error signing out');
    throw error;
  }
};

export const resetPassword = async (email: string) => {
  try {
    if (!email) {
      throw new Error('Email is required');
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Please enter a valid email address');
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth?mode=reset`,
    });
    
    if (error) {
      throw error;
    }
    
    toast.success('Password reset link sent to your email');
  } catch (error: any) {
    adminError('Password reset error:', error);
    toast.error(error.message || 'Error sending reset link');
    throw error;
  }
};
